<script setup lang="ts">
import {
	ref,
	watch,
	computed,
	onMounted,
	onBeforeUnmount,
	StyleValue,
	nextTick,
} from 'vue';
import { useRouter } from 'vue-router';
import { useSiteGlobal } from '@zyro-inc/site-modules/use/useSiteGlobal';
import { SiteBlockStickyBar } from '@hostinger/builder-schema-validator/schema/schemaTypes';
import {
	BLOCK_HEADER_CLASS,
	ELEMENT_POSITION_KEY_DESKTOP,
	ELEMENT_POSITION_KEY_MOBILE,
	SYSTEM_LOCALE,
	BLOCK_STICKY_BAR_ID,
	PAGE_TYPE_PRIVATE,
	DATA_ATTRIBUTE_PREVENT_NATIVE_CLICK,
	MEDIA_MOBILE_SCREEN_BREAKPOINT,
	AI_PREVIEW_ROUTE,
	BLOCK_SLOT_FOOTER,
	BLOCK_SLOT_HEADER,
	PROPERTY_SLOTS_ARE_HIDDEN,
	BLOCK_TYPE_APPOINTMENT,
} from '@zyro-inc/site-modules/constants/siteModulesConstants';
import EventLogApi from '@zyro-inc/site-modules/api/EventLogApi';

import StickyTrigger from '@zyro-inc/site-modules/components/StickyTrigger.vue';
import { scrollToSection } from '@zyro-inc/site-modules/utils/scrollToSection';
import { getGridItemSize } from '@zyro-inc/site-modules/utils/getGridItemSize';
import { objectToCssVariablesDeprecated } from '@zyro-inc/site-modules/utils/objectToCssVariablesDeprecated';

import CookieBanner from '@zyro-inc/site-modules/components/CookieBanner.vue';
import BlockUser from '@zyro-inc/site-modules/components/BlockUser.vue';
import BlockStickyBarProviderUser from '@zyro-inc/site-modules/components/blocks/BlockStickyBarProviderUser.vue';
import PasswordPage from '@zyro-inc/site-modules/components/password-page/PasswordPage.vue';
import EcommerceShoppingCartProviderUser from '@zyro-inc/site-modules/components/ecommerce/EcommerceShoppingCartProviderUser.vue';
import EcommerceModalRoot from '@zyro-inc/site-modules/components/ecommerce/modals/EcommerceModalRoot.vue';
import BlockHeader from '@zyro-inc/site-modules/components/BlockHeader.vue';
import { getHeaderProps } from '@zyro-inc/site-modules/components/blocks/header/getHeaderProps';
import { useLightbox } from '@zyro-inc/site-modules/components/lightbox/useLightbox';
import Lightbox from '@zyro-inc/site-modules/components/lightbox/Lightbox.vue';
import { getIsInIframe } from '@zyro-inc/site-modules/utils/getIsInIframe';
import { useSiteEngineAnimations } from '@zyro-inc/site-modules/use/useSiteEngineAnimations';
import { useEcommerceGlobal } from '@zyro-inc/site-modules/use/useEcommerceGlobal';
import { useElementBounding } from '@vueuse/core';
import UpgradeBanner from '@zyro-inc/site-modules/components/metas/UpgradeBanner.vue';
import {
	SitePageData,
	EcommerceProduct,
} from '@zyro-inc/site-modules/types';

type PixelUnit = `${number}px`;

type HeaderHeightStyles = {
	'--header-height'?: PixelUnit;
	'--header-height-mobile'?: PixelUnit;
}

type Props = {
	pageData: SitePageData;
	productData?: EcommerceProduct;
	isInPreviewMode?: boolean;
	isPreviewMobileView?: boolean;
	currentPreviewProductPageId?: string;
}

const props = defineProps<Props>();

const {
	pages,
	blocks,
	elements,
	nav,
	meta,
	metaTitle,
	ecommerceShoppingCart,
	cookieBannerDisclaimer,
	cookieBannerAcceptText,
	cookieBannerDeclineText,
	currentLocale,
	languageSwitcherLanguages,
	siteId,
	homePageId,
	styles,
	currentPageId,
	currentPageData: currentPage,
	getPagePathFromId,
	setPageData,
	setProductData,
	isDynamicProductPageEnabled,
} = useSiteGlobal();

const {
	isStoreTypeZyro,
	shoppingCartItemCount,
	setShoppingCartOpen,
	getAreAllEcommerceProductListVisibleBlocksLoaded,
	isShoppingCartOpen,
	getStoreSettingsData,
	getCartData,
	isExpressCheckoutEnabled,
} = useEcommerceGlobal();

const { isLightboxOpen } = useLightbox();
const router = useRouter();
const { shouldMountAnimationsInPreview } = useSiteEngineAnimations();

const isNavOpen = ref(false);
const blockListRef = ref(null);
const stickyBarHeight = ref(0);

// on some screen width (tablet), font style, menu item length combinations default height extends to fit navigation
// and header height needs to be adjusted - set to headerHeightDynamic
const headerHeightDynamic = ref(0);
const resizeObserver = ref<ResizeObserver>(null as unknown as ResizeObserver);

const { width: screenWidth } = useElementBounding(blockListRef);

const isMobile = computed(() => screenWidth.value <= MEDIA_MOBILE_SCREEN_BREAKPOINT);
const currentPageType = computed(() => currentPage.value.type);
const isCurrentPagePrivate = computed(() => currentPageType.value === PAGE_TYPE_PRIVATE);
const pageCSSVars = computed(() => objectToCssVariablesDeprecated(styles.value) as StyleValue);
const isHeaderVisible = computed(
	() => !currentPage.value[PROPERTY_SLOTS_ARE_HIDDEN]?.includes(BLOCK_SLOT_HEADER) && !isCurrentPagePrivate.value,
);
const isStickyBarVisible = computed(() => blocks.value[BLOCK_STICKY_BAR_ID] && !isCurrentPagePrivate.value);
const isCurrentPageHomepage = computed(() => currentPageId.value === homePageId.value);
const homepageName = computed(() => pages.value?.[homePageId.value]?.name ?? '');
// @ts-ignore json-validation-related
const isHeaderSettingsSticky = computed(() => blocks.value.header.settings.isSticky);
const shouldRenderPage = computed(() => !currentPage.value?.meta?.password || props.isInPreviewMode);
const pageBlocksSlotFooter = computed(() => {
	if (!currentPage.value || currentPage.value[PROPERTY_SLOTS_ARE_HIDDEN]?.includes(BLOCK_SLOT_FOOTER) || isCurrentPagePrivate.value) {
		return [];
	}

	// @ts-ignore json-validation-related
	const footerBlock = Object.keys(blocks.value).find((blockId) => blocks.value[blockId].slot === 'footer');

	return footerBlock ? [footerBlock] : [];
});

const currentPageBlocks = computed(() => (currentPage.value ? [
	...currentPage.value.blocks.filter((blockId: string) => {
		const isBlockDefined = Object.keys(blocks.value).includes(blockId);

		// prevent astro builds failing when block is not defined in blocks but present in page data
		if (!isBlockDefined) {
			return false;
		}

		return blockId !== BLOCK_STICKY_BAR_ID;
	}),
	...pageBlocksSlotFooter.value,
] : []));

// @ts-ignore until we convert getHeaderProps to ts
const headerProps = computed(() => getHeaderProps({
	siteId: siteId.value,
	meta: meta.value,
	blocks: blocks.value,
	nav: nav.value,
	pages: pages.value,
	elements: elements.value,
	languageMetaTitle: metaTitle.value,
	currentLocale: currentLocale.value,
	currentPageId: currentPageId.value,
	shoppingCartItemCount: shoppingCartItemCount.value,
	languageSwitcherLanguages: languageSwitcherLanguages.value,
	getPagePathFromId: ({ pageId }: { pageId: string }) => getPagePathFromId({
		pageId,
	}),
	isOpen: isNavOpen.value,
}));

const headerHeight = computed(() => headerProps.value.height);
const headerHeightMobile = computed(() => headerProps.value.heightMobile);
const topBarHeightMobilePx = computed(() => `${headerHeightMobile.value + stickyBarHeight.value}px`);
const defaultLocale = computed(() => meta.value.defaultLocale ?? SYSTEM_LOCALE);
const isUpgradeBannerEnabled = computed(() => meta.value.isUpgradeBannerEnabled);
const isWhatsAppEnabled = computed(() => !!meta.value.whatsAppNumber);
const ecommerceTranslations = computed(() => {
	if (!isStoreTypeZyro.value) {
		return {};
	}

	return ecommerceShoppingCart.value?.translations ?? {};
});
const language = computed(() => {
	if (!isStoreTypeZyro.value) {
		return null;
	}

	return ecommerceShoppingCart.value?.lang ?? 'en';
});

const firstVisibleDesktopBlockId = computed(
	// @ts-ignore json-validation-related
	() => currentPageBlocks.value.find((blockId) => !blocks.value[blockId][ELEMENT_POSITION_KEY_DESKTOP]?.isHidden),
);

const firstVisibleMobileBlockId = computed(
	// @ts-ignore json-validation-related
	() => currentPageBlocks.value.find((blockId) => !blocks.value[blockId][ELEMENT_POSITION_KEY_MOBILE]?.isHidden),
);

const visibleMobileHeaderHeight = computed((): PixelUnit | undefined => {
	// @ts-ignore json-validation-related
	const { isTransparent } = blocks.value.header.background ?? {};

	if (isTransparent && isHeaderVisible.value && headerHeightMobile.value) {
		const height = headerHeightDynamic.value > headerHeightMobile.value
			? headerHeightDynamic.value
			: headerHeightMobile.value;

		return `${height}px`;
	}

	return undefined;
});

const visibleDesktopHeaderHeight = computed((): PixelUnit | undefined => {
	// @ts-ignore json-validation-related
	const { isTransparent } = blocks.value.header.background ?? {};

	if (isTransparent && isHeaderVisible.value && headerHeight.value) {
		const height = headerHeightDynamic.value > headerHeight.value ? headerHeightDynamic.value : headerHeight.value;

		return `${height}px`;
	}

	return undefined;
});

// Return mobile/desktop header heights only if block is first visible
const getHeaderHeightStyles = (blockId: string): HeaderHeightStyles => ({
	...(firstVisibleDesktopBlockId.value === blockId && {
		'--header-height': visibleDesktopHeaderHeight.value,
	}),
	...(firstVisibleMobileBlockId.value === blockId && {
		'--header-height-mobile': visibleMobileHeaderHeight.value,
	}),
});

const lcp = computed(() => {
	const [firstBlockId] = currentPage.value?.blocks ?? [];

	// @ts-ignore json-validation-related
	if (blocks.value?.[firstBlockId]?.background?.current === 'image') {
		return {
			type: 'block-background',
			id: firstBlockId,
		};
	}

	if (blocks.value?.[firstBlockId]?.type === 'BlockBlogList') {
		return {
			type: 'block-blog-list',
			id: firstBlockId,
		};
	}

	if (blocks.value?.[firstBlockId]?.type === BLOCK_TYPE_APPOINTMENT) {
		return {
			type: 'block-appointment',
			id: firstBlockId,
		};
	}

	if (blocks.value?.[firstBlockId]?.type === 'BlockEcommerceProduct') {
		return {
			type: 'block-ecommerce-product',
			id: firstBlockId,
		};
	}

	if (blocks.value?.[firstBlockId]?.type === 'BlockEcommerceProductList') {
		return {
			type: 'block-ecommerce-product-list',
			id: firstBlockId,
		};
	}

	if (blocks.value?.[firstBlockId]?.type === 'BlockAppointmentList') {
		return {
			type: 'block-appointment-list',
			id: firstBlockId,
		};
	}

	// this should return [{ blockId, elementId }, { blockId, elementId }, ...]
	// because we need both blockId and elementId to get image size
	const allElementIds = currentPageBlocks.value
		.filter((blockId) => (blocks?.value?.[blockId]?.components?.length ?? 0) > 0)
		.flatMap((blockId) => (blocks?.value?.[blockId]?.components ?? []).map((elementId: string) => ({
			blockId,
			elementId,
		})));

	const largestImage = allElementIds
		.filter(({ elementId }) => elements.value[elementId]?.type === 'GridImage')
		.map(({
			blockId,
			elementId,
		}) => {
			const elementData = elements.value[elementId];

			// Check wether image has 'mobile' or 'desktop' width/height
			// If yes, use them to calculate LCP, otherwise it's ecommerce element and calculate size via `getGridItemSize`
			const sizeInLayout = elementData.mobile ?? elementData.desktop;

			const {
				width,
				height,
			} = sizeInLayout ?? getGridItemSize(
				blocks.value[blockId],
				elementData.settings.styles.position,
			);

			return {
				elementId,
				imageRatio: height / width,
			};
		})[0];

	return largestImage ? {
		type: 'grid-image',
		id: largestImage?.elementId,
	} : {};
});

const scrollToHash = async ({
	hash,
	isPreviewMode,
}:{hash: string, isPreviewMode: boolean}) => {
	if (!getAreAllEcommerceProductListVisibleBlocksLoaded(isMobile.value)) {
		await new Promise((resolve) => {
			const interval = setInterval(() => {
				if (getAreAllEcommerceProductListVisibleBlocksLoaded(isMobile.value)) {
					clearInterval(interval);

					resolve(null);
				}
			}, 50);
		});
	}

	await nextTick();

	if (!hash) {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: 'smooth',
		});
	}

	scrollToSection({
		linkToSection: hash,
		isPreviewMode,
	});
};

const redirectToThirdPartyLink = (anchorElement: HTMLAnchorElement) => {
	const {
		target,
		href,
	} = anchorElement;
	const shouldOpenInNewTab = target === '_blank';
	const linkOpenMode = shouldOpenInNewTab || getIsInIframe() || props.isInPreviewMode ? '_blank' : '_self';

	window.open(href, linkOpenMode);
};

const handleGlobalClick = async (event: Event) => {
	if (!event.target) {
		return;
	}

	const closestAnchor = (event.target as HTMLElement).closest('a');

	if (!closestAnchor) {
		return;
	}

	const {
		href,
		pathname,
		origin,
		hash,
		target,
	} = closestAnchor;

	if (!href || closestAnchor.hasAttribute(DATA_ATTRIBUTE_PREVENT_NATIVE_CLICK)) {
		return;
	}

	event.preventDefault();

	const isTargetThirdParty = window.location.origin !== origin;

	if (isTargetThirdParty) {
		redirectToThirdPartyLink(closestAnchor);

		return;
	}

	const isTargetPageCurrentPage = window.location.pathname === pathname;
	const shouldOpenInNewTab = target === '_blank';
	const fullPath = href.replace(origin, '');
	const currentFullPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
	const isAiBuilderPreview = computed(() => router?.currentRoute?.value.name === AI_PREVIEW_ROUTE);

	if (isTargetPageCurrentPage && !shouldOpenInNewTab) {
		scrollToHash({
			hash,
			isPreviewMode: props.isInPreviewMode,
		});
	}

	if ((shouldOpenInNewTab && !props.isInPreviewMode) && !getIsInIframe()) {
		window.open(href, target);
	} else if ((shouldOpenInNewTab && props.isInPreviewMode) || currentFullPath !== fullPath) {
		if (props.isInPreviewMode || isAiBuilderPreview.value) {
			const pathWithoutHash = fullPath.split('#')[0];

			await router.push(pathWithoutHash);
		} else {
			window.location.assign(fullPath);
		}

		if (hash) {
			scrollToHash({
				hash,
				isPreviewMode: props.isInPreviewMode,
			});
		}

		isNavOpen.value = false;
	}
};

const getEcommerceData = () => {
	if (!isStoreTypeZyro.value) {
		return;
	}

	getStoreSettingsData();
	getCartData();
};

// Since `pageData` is set to state during mount, it has to be updated via watcher every time the prop changes.
watch(() => props.pageData, (pageData) => {
	setPageData(pageData);
}, {
	immediate: true,
});

watch(() => props.productData, (productData) => {
	setProductData(productData);
}, {
	immediate: true,
});

// The first watcher has an `immediate` watcher - so it performs server-side compatible actions
// This watcher has no immediate - so it will run only on the client.
watch(() => props.pageData, async () => {
	if (props.isInPreviewMode) {
		shouldMountAnimationsInPreview.value = true;
	}
});

onMounted(async () => {
	if (!props.isInPreviewMode) {
		EventLogApi.initAmplitude({
			siteId: siteId.value,
		});
	}

	await getEcommerceData();

	stickyBarHeight.value = document.querySelector('.block-sticky-bar')?.clientHeight ?? 0;
	const { hash } = window.location;

	if (hash) {
		scrollToHash({
			hash,
			isPreviewMode: props.isInPreviewMode,
		});
	}

	const blockHeaderElement = document.querySelector(`.${BLOCK_HEADER_CLASS}`) as HTMLElement;

	if (blockHeaderElement) {
		resizeObserver.value = new ResizeObserver(() => {
			headerHeightDynamic.value = blockHeaderElement.offsetHeight;
		});
		resizeObserver.value.observe(blockHeaderElement);
	}
});

onBeforeUnmount(() => {
	resizeObserver.value?.disconnect();
});
</script>
<template>
	<main
		v-if="shouldRenderPage"
		:style="pageCSSVars"
		class="page"
		@click="handleGlobalClick"
	>
		<StickyTrigger v-if="isHeaderSettingsSticky" />
		<BlockStickyBarProviderUser
			v-if="!isHeaderSettingsSticky && isStickyBarVisible"
			:data-block-id="BLOCK_STICKY_BAR_ID"
			:data="blocks[BLOCK_STICKY_BAR_ID] as SiteBlockStickyBar"
			:components="elements"
			:lcp="lcp"
		/>
		<div
			class="top-blocks"
			:class="{
				'top-blocks--sticky': headerProps.isSticky,
				'top-blocks--preview-mode': props.isInPreviewMode,
			}"
		>
			<BlockStickyBarProviderUser
				v-if="isHeaderSettingsSticky && isStickyBarVisible"
				:data-block-id="BLOCK_STICKY_BAR_ID"
				:data="blocks[BLOCK_STICKY_BAR_ID] as SiteBlockStickyBar"
				:components="elements"
				:lcp="lcp"
			/>
			<BlockHeader
				v-if="isHeaderVisible"
				v-bind="headerProps"
				:is-in-preview-mode="props.isInPreviewMode"
				:is-preview-mobile-view="isPreviewMobileView"
				:block-sticky-bar="blocks[BLOCK_STICKY_BAR_ID]"
				@toggle-visibility="isNavOpen = !isNavOpen"
				@cart-click="setShoppingCartOpen(!isShoppingCartOpen, 'cart_icon')"
			/>
		</div>
		<!-- Simple blocks -->
		<div
			ref="blockListRef"
			class="page__blocks"
		>
			<BlockUser
				v-for="(blockId) of currentPageBlocks"
				:id="(blocks[blockId] as any)?.htmlId || blockId"
				:key="`${currentPageId}-${blockId}`"
				:data="blocks[blockId]"
				:blocks="blocks"
				:lcp="lcp"
				:page-id="currentPageId"
				:ecommerce-translations="ecommerceTranslations"
				:current-page-type="currentPageType"
				:components="elements"
				:screen-width="screenWidth"
				:style="getHeaderHeightStyles(blockId)"
				:current-locale="currentLocale"
				:is-cart-visible="headerProps.isCartVisible"
				:is-in-preview-mode="props.isInPreviewMode"
				:is-first-visible-desktop-block="firstVisibleDesktopBlockId === blockId"
				:is-first-visible-mobile-block="firstVisibleMobileBlockId === blockId"
				:is-dynamic-product-page-enabled="isDynamicProductPageEnabled"
				:current-preview-product-page-id="currentPreviewProductPageId"
				:is-whats-app-enabled="isWhatsAppEnabled"
			/>
		</div>

		<CookieBanner
			v-if="meta.isCookieBarEnabled && !isCurrentPagePrivate"
			:disclaimer="cookieBannerDisclaimer || ''"
			:accept-text="cookieBannerAcceptText"
			:decline-text="cookieBannerDeclineText"
			:site-meta="meta"
		/>
		<EcommerceShoppingCartProviderUser
			v-if="headerProps.isCartVisible"
			:ecommerce-translations="ecommerceTranslations"
			:language="language"
			:sticky-bar-height="stickyBarHeight"
			:is-header-sticky="headerProps.isSticky"
			:is-header-hidden="!isHeaderVisible"
			:is-in-preview-mode="props.isInPreviewMode"
			:is-mobile="isMobile"
			:is-express-checkout-enabled="isExpressCheckoutEnabled"
		/>
		<EcommerceModalRoot
			:ecommerce-translations="ecommerceTranslations"
			:language="language"
			:current-locale="currentLocale"
			:is-cart-visible="headerProps.isCartVisible"
			:is-in-preview-mode="props.isInPreviewMode"
		/>
	</main>
	<PasswordPage
		v-else
		:site-id="siteId"
		:page-id="currentPageId"
		:locale="currentLocale"
		:default-locale="defaultLocale"
		:in-preview-mode="isInPreviewMode"
		:current-page-data="currentPage"
		:homepage-name="homepageName"
		:is-current-page-homepage="isCurrentPageHomepage"
	/>
	<Lightbox v-if="isLightboxOpen" />
	<UpgradeBanner
		v-if="isUpgradeBannerEnabled"
		:is-mobile="isMobile"
	/>
</template>

<style lang="scss">
.page {
	display: flex;
	flex-direction: column;
	min-height: 100vh;

	&__blocks {
		display: flex;
		flex-direction: column;
		flex-grow: 1;

        & > .block:last-child:not(.block--footer),
        & > .block:not(.block--footer):nth-last-child(2):has(+ .block--footer) {
			flex-grow: 1;
		}
	}
}

.top-blocks {
	z-index: $z-index-site-engine-header;

	&--sticky {
		position: sticky;
		top: 0;
	}

	&--preview-mode {
		.block-header-layout-mobile__dropdown--open.block-header-layout-mobile__dropdown--full-screen:not(.s) {
			height: calc(793px - v-bind(topBarHeightMobilePx));
		}
	}
}
</style>
